// Enhanced Text-to-Speech Service for Chinese Text
// Supports both Cantonese and Mandarin pronunciation with improved quality

class TextToSpeechService {
  constructor() {
    this.speechSynthesis = window.speechSynthesis;
    this.currentUtterance = null;
    this.isPlaying = false;
    this.availableVoices = [];
    this.voiceCache = new Map();
    this.currentPlayingId = null; // Track which component is currently playing
    this.qualitySettings = {
      rate: 0.75,        // Slower for better pronunciation
      pitch: 1.0,        // Natural pitch
      volume: 1.0,       // Full volume
      preferredVoices: {
        mandarin: ['zh-CN', 'cmn-CN', 'zh-TW', 'cmn-TW'],
        cantonese: ['zh-HK', 'yue-HK', 'zh-HK-HK', 'yue-HK-HK']
      }
    };
  }

  // Initialize and cache available voices
  async initialize() {
    try {
      this.availableVoices = await this.getVoices();
      this.cacheVoicePreferences();
      console.log('TTS Service initialized with', this.availableVoices.length, 'voices');
    } catch (error) {
      console.error('Failed to initialize TTS service:', error);
    }
  }

  // Get available voices with better error handling
  getVoices() {
    return new Promise((resolve) => {
      let voices = this.speechSynthesis.getVoices();
      
      if (voices.length > 0) {
        resolve(voices);
      } else {
        // Wait for voices to load
        const checkVoices = () => {
          voices = this.speechSynthesis.getVoices();
          if (voices.length > 0) {
            resolve(voices);
          } else {
            setTimeout(checkVoices, 100);
          }
        };
        
        this.speechSynthesis.onvoiceschanged = () => {
          voices = this.speechSynthesis.getVoices();
          resolve(voices);
        };
        
        // Fallback timeout
        setTimeout(() => {
          voices = this.speechSynthesis.getVoices();
          resolve(voices);
        }, 2000);
      }
    });
  }

  // Cache voice preferences for better performance
  cacheVoicePreferences() {
    const chineseVoices = this.availableVoices.filter(voice => 
      voice.lang.includes('zh') || 
      voice.lang.includes('cmn') || 
      voice.lang.includes('yue') ||
      voice.name.toLowerCase().includes('chinese') ||
      voice.name.toLowerCase().includes('mandarin') ||
      voice.name.toLowerCase().includes('cantonese')
    );

    // Sort voices by quality (prefer native Chinese voices)
    const sortedVoices = chineseVoices.sort((a, b) => {
      const aScore = this.getVoiceQualityScore(a);
      const bScore = this.getVoiceQualityScore(b);
      return bScore - aScore;
    });

    this.voiceCache.set('all', sortedVoices);
    this.voiceCache.set('mandarin', sortedVoices.filter(v => 
      v.lang.includes('cmn') || 
      v.lang.includes('zh-CN') || 
      v.lang.includes('zh-TW') ||
      v.name.toLowerCase().includes('mandarin')
    ));
    this.voiceCache.set('cantonese', sortedVoices.filter(v => 
      v.lang.includes('yue') || 
      v.lang.includes('zh-HK') ||
      v.name.toLowerCase().includes('cantonese')
    ));
  }

  // Score voice quality based on various factors
  getVoiceQualityScore(voice) {
    let score = 0;
    
    // Prefer native Chinese voices
    if (voice.lang.includes('zh') || voice.lang.includes('cmn') || voice.lang.includes('yue')) {
      score += 100;
    }
    
    // Prefer specific language codes
    if (voice.lang.includes('zh-CN') || voice.lang.includes('cmn-CN')) {
      score += 50; // Mandarin
    }
    if (voice.lang.includes('zh-HK') || voice.lang.includes('yue-HK')) {
      score += 50; // Cantonese
    }
    
    // Prefer voices with Chinese names
    if (voice.name.toLowerCase().includes('chinese')) {
      score += 30;
    }
    if (voice.name.toLowerCase().includes('mandarin')) {
      score += 40;
    }
    if (voice.name.toLowerCase().includes('cantonese')) {
      score += 40;
    }
    
    // Prefer voices with specific keywords
    if (voice.name.toLowerCase().includes('xiaoyi') || 
        voice.name.toLowerCase().includes('yaya') ||
        voice.name.toLowerCase().includes('kangkang')) {
      score += 60; // Microsoft Chinese voices
    }
    
    return score;
  }

  // Find the best available Chinese voices
  async findChineseVoices() {
    if (this.availableVoices.length === 0) {
      await this.initialize();
    }

    const mandarinVoices = this.voiceCache.get('mandarin') || [];
    const cantoneseVoices = this.voiceCache.get('cantonese') || [];
    const allChineseVoices = this.voiceCache.get('all') || [];

    return {
      mandarin: mandarinVoices[0] || allChineseVoices[0],
      cantonese: cantoneseVoices[0] || allChineseVoices[0],
      default: allChineseVoices[0] || this.availableVoices.find(voice => voice.lang.includes('zh')) || this.availableVoices[0]
    };
  }

  // Enhanced text preprocessing for better pronunciation
  preprocessText(text) {
    if (!text) return '';
    
    // Remove extra whitespace and normalize
    let processed = text.trim().replace(/\s+/g, ' ');
    
    // Add pauses for better rhythm in classical Chinese
    processed = processed.replace(/([。！？；])/g, '$1 ');
    
    // Handle special characters that might affect pronunciation
    processed = processed.replace(/[「」『』]/g, '"');
    processed = processed.replace(/[（）]/g, '()');
    
    return processed;
  }

  // Speak text with enhanced quality settings and individual control
  async speakText(text, language = 'mandarin', componentId = null) {
    if (!text || !text.trim()) {
      console.warn('No text provided for speech');
      return;
    }

    // Stop any currently playing speech first
    this.stop();

    try {
      // Initialize if not already done
      if (this.availableVoices.length === 0) {
        await this.initialize();
      }

      const voices = await this.findChineseVoices();
      let selectedVoice = voices.default;

      if (language === 'mandarin' && voices.mandarin) {
        selectedVoice = voices.mandarin;
      } else if (language === 'cantonese' && voices.cantonese) {
        selectedVoice = voices.cantonese;
      }

      // Preprocess text for better pronunciation
      const processedText = this.preprocessText(text);

      const utterance = new SpeechSynthesisUtterance(processedText);
      
      // Enhanced quality settings
      utterance.voice = selectedVoice;
      utterance.lang = selectedVoice.lang || 'zh-CN';
      utterance.rate = this.qualitySettings.rate;
      utterance.pitch = this.qualitySettings.pitch;
      utterance.volume = this.qualitySettings.volume;

      // Add slight pauses for better comprehension
      utterance.onboundary = (event) => {
        if (event.name === 'sentence') {
          // Natural pause at sentence boundaries
        }
      };

      // Event handlers with better error handling
      utterance.onstart = () => {
        this.isPlaying = true;
        this.currentUtterance = utterance;
        this.currentPlayingId = componentId;
        console.log('Speech started with voice:', selectedVoice.name, 'language:', selectedVoice.lang, 'componentId:', componentId);
      };

      utterance.onend = () => {
        this.isPlaying = false;
        this.currentUtterance = null;
        this.currentPlayingId = null;
        console.log('Speech ended successfully');
      };

      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
        this.isPlaying = false;
        this.currentUtterance = null;
        this.currentPlayingId = null;
        
        // Try fallback voice if available
        if (event.error === 'not-allowed' || event.error === 'network') {
          this.tryFallbackVoice(processedText, language, componentId);
        }
      };

      // Start speaking
      this.speechSynthesis.speak(utterance);
      
    } catch (error) {
      console.error('Error in text-to-speech:', error);
      // Enhanced fallback
      this.tryFallbackVoice(text, language, componentId);
    }
  }

  // Try fallback voice with different settings
  async tryFallbackVoice(text, language, componentId = null) {
    try {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = language === 'mandarin' ? 'zh-CN' : 'zh-HK';
      utterance.rate = 0.8; // Slightly faster fallback
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      utterance.onstart = () => {
        this.isPlaying = true;
        this.currentUtterance = utterance;
        this.currentPlayingId = componentId;
        console.log('Using fallback voice');
      };
      
      utterance.onend = () => {
        this.isPlaying = false;
        this.currentUtterance = null;
        this.currentPlayingId = null;
      };
      
      utterance.onerror = (event) => {
        console.error('Fallback voice also failed:', event);
        this.isPlaying = false;
        this.currentUtterance = null;
        this.currentPlayingId = null;
      };
      
      this.speechSynthesis.speak(utterance);
    } catch (error) {
      console.error('Fallback voice failed:', error);
      this.isPlaying = false;
      this.currentPlayingId = null;
    }
  }

  // Stop current speech
  stop() {
    if (this.isPlaying) {
      this.speechSynthesis.cancel();
      this.isPlaying = false;
      this.currentUtterance = null;
      this.currentPlayingId = null;
    }
  }

  // Check if speech is currently playing
  isCurrentlyPlaying() {
    return this.isPlaying;
  }

  // Check if a specific component is currently playing
  isComponentPlaying(componentId) {
    return this.isPlaying && this.currentPlayingId === componentId;
  }

  // Pause speech
  pause() {
    if (this.isPlaying) {
      this.speechSynthesis.pause();
    }
  }

  // Resume speech
  resume() {
    if (this.isPlaying) {
      this.speechSynthesis.resume();
    }
  }

  // Get available voice information for debugging
  getVoiceInfo() {
    return {
      totalVoices: this.availableVoices.length,
      chineseVoices: this.voiceCache.get('all')?.length || 0,
      mandarinVoices: this.voiceCache.get('mandarin')?.length || 0,
      cantoneseVoices: this.voiceCache.get('cantonese')?.length || 0,
      voices: this.availableVoices.map(v => ({
        name: v.name,
        lang: v.lang,
        default: v.default
      }))
    };
  }

  // Update quality settings
  updateQualitySettings(settings) {
    this.qualitySettings = { ...this.qualitySettings, ...settings };
  }
}

// Create singleton instance
const textToSpeechService = new TextToSpeechService();

// Initialize the service when the module loads
textToSpeechService.initialize();

export default textToSpeechService; 